import {createFileRoute} from '@tanstack/react-router';
import {useRef, useState, useEffect} from 'react';
import {useAuth, useAuthActions} from '@/components/AuthProvider';
import {updateProfile} from '@/server/auth';
import {getProfileName} from '@/utils/getProfileName';
import {AvatarUploader} from '@/components/avatarUploader/avatarUploader';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Typography} from '@/components/ui/typography';
import {Loader2, Check} from 'lucide-react';

export const Route = createFileRoute('/dashboard/settings')({
  component: SettingsPage,
});

/**
 * Settings section of the profile dashboard.
 * Shows profile info and a form for editing name and avatar.
 */
function SettingsPage() {
  const {profile} = useAuth();
  const {invalidate} = useAuthActions();

  if (!profile) return null;

  return (
    <div className="flex flex-col gap-6">
      <Typography level="H3">Settings</Typography>
      <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-6 flex items-center gap-4">
          <ProfileAvatar
            avatarUrl={profile.avatarUrl}
            name={getProfileName(profile)}
          />
          <div>
            <Typography level="B1" className="font-medium text-slate-900">
              {getProfileName(profile)}
            </Typography>
            <Typography level="B2" className="text-slate-500">
              {profile.email}
            </Typography>
          </div>
        </div>

        <hr className="mb-6 border-slate-200" />

        <ProfileForm
          currentFirstName={profile.firstName ?? ''}
          currentLastName={profile.lastName ?? ''}
          onSaved={invalidate}
        />
      </div>
    </div>
  );
}

interface ProfileAvatarProps {
  avatarUrl: string | null;
  name: string;
}

/**
 * Displays the user's avatar image or a fallback with their initial.
 */
function ProfileAvatar({avatarUrl, name}: ProfileAvatarProps) {
  const initial = name.charAt(0).toUpperCase();

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className="h-14 w-14 rounded-full object-cover"
        referrerPolicy="no-referrer"
      />
    );
  }

  return (
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary">
      {initial}
    </div>
  );
}

interface ProfileFormProps {
  currentFirstName: string;
  currentLastName: string;
  onSaved: () => Promise<void>;
}

/**
 * Inline form to edit the user's first name, last name, and profile photo.
 * Avatar uploads are held as pending state until the user submits the form.
 */
function ProfileForm({
  currentFirstName,
  currentLastName,
  onSaved,
}: ProfileFormProps) {
  const [firstName, setFirstName] = useState(currentFirstName);
  const [lastName, setLastName] = useState(currentLastName);
  const [pendingAvatarUrl, setPendingAvatarUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => clearTimeout(savedTimerRef.current);
  }, []);

  const trimmedFirstName = firstName.trim();
  const trimmedLastName = lastName.trim();
  const firstNameChanged = trimmedFirstName !== currentFirstName;
  const lastNameChanged = trimmedLastName !== currentLastName;
  const avatarChanged = pendingAvatarUrl !== null;
  const hasChanges = firstNameChanged || lastNameChanged || avatarChanged;
  const changedNamesValid =
    (!firstNameChanged || trimmedFirstName !== '') &&
    (!lastNameChanged || trimmedLastName !== '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!changedNamesValid || !hasChanges) return;

    setIsSubmitting(true);
    setError(null);
    setSaved(false);

    try {
      const payload: {
        firstName?: string;
        lastName?: string;
        avatarUrl?: string;
      } = {};
      if (firstNameChanged) payload.firstName = trimmedFirstName;
      if (lastNameChanged) payload.lastName = trimmedLastName;
      if (avatarChanged && pendingAvatarUrl) {
        payload.avatarUrl = pendingAvatarUrl;
      }

      await updateProfile({data: payload});
      await onSaved();
      setPendingAvatarUrl(null);
      setSaved(true);
      savedTimerRef.current = setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="firstName">First Name</Label>
          <Input
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            maxLength={100}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            id="lastName"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
            maxLength={100}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Profile Photo</Label>
        <AvatarUploader
          pendingAvatarUrl={pendingAvatarUrl}
          onChange={setPendingAvatarUrl}
        />
      </div>

      {error && (
        <Typography
          level="B2"
          className="rounded-lg bg-red-50 px-3 py-2 text-red-600"
        >
          {error}
        </Typography>
      )}

      <Button
        type="submit"
        size="default"
        disabled={isSubmitting || !changedNamesValid || !hasChanges}
      >
        {isSubmitting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : saved ? (
          <Check className="h-4 w-4" />
        ) : null}
        {saved ? 'Saved' : 'Save Changes'}
      </Button>
    </form>
  );
}
